import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { AppLayout } from './components/layout/AppLayout';
import { HomeView } from './views/HomeView';
import { DashboardView } from './views/DashboardView';
import { TableView } from './views/TableView';
import { AnalyticsView } from './views/AnalyticsView';
import { DeliveryLogsView } from './views/DeliveryLogsView';
import { RulesView } from './views/RulesView';
import { SettingsView } from './views/SettingsView';
import { ProjectDatastoresView } from './views/ProjectDatastoresView';
import { AccountView } from './views/AccountView';
import { OrganizationView } from './views/OrganizationView';
import { AdminView } from './views/AdminView';
import { AdminOrganizationDetailView } from './views/AdminOrganizationDetailView';
import { TenantDetailView } from './views/TenantDetailView';
import { AdminPlansView } from './views/AdminPlansView';
import { LoginView } from './views/LoginView';
import { RegisterView } from './views/RegisterView';
import { ForgotPasswordView } from './views/ForgotPasswordView';
import { ResetPasswordView } from './views/ResetPasswordView';
import { InviteAcceptView } from './views/InviteAcceptView';

const RouteSynchronizer: React.FC = () => {
  const location = useLocation();
  const {
    organizations,
    currentOrg,
    switchOrganization,
    projects,
    currentProject,
    setCurrentProject,
    tables,
    currentTable,
    setCurrentTable,
  } = useAuth();

  useEffect(() => {
    const pathname = location.pathname;
    const orgMatch = pathname.match(/\/organization\/([^/]+)/);
    if (orgMatch && orgMatch[1]) {
      const targetOrgId = orgMatch[1];
      if (currentOrg && currentOrg.id !== targetOrgId) {
        const foundOrg = organizations.find((o) => o.id === targetOrgId);
        if (foundOrg) {
          switchOrganization(foundOrg);
        }
      }
    }

    const projMatch = pathname.match(/\/projects\/([^/]+)/);
    if (projMatch && projMatch[1]) {
      const targetProjId = projMatch[1];
      if (currentProject?.id !== targetProjId) {
        const foundProj = projects.find((p) => p.id === targetProjId);
        if (foundProj) {
          setCurrentProject(foundProj);
        }
      }
    } else if (pathname.startsWith('/organization/') && !pathname.includes('/projects/')) {
      if (currentProject) {
        setCurrentProject(null);
      }
    }

    const tableMatch = pathname.match(/\/tables\/([^/]+)/);
    if (tableMatch && tableMatch[1]) {
      const targetTableId = tableMatch[1];
      if (currentTable?.id !== targetTableId) {
        const foundTable = tables.find((t) => t.id === targetTableId);
        if (foundTable) {
          setCurrentTable(foundTable);
        }
      }
    }
  }, [
    location.pathname,
    organizations,
    currentOrg?.id,
    projects,
    currentProject?.id,
    tables,
    currentTable?.id,
  ]);

  return null;
};

const ProtectedLayout: React.FC = () => {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return (
    <>
      <RouteSynchronizer />
      <AppLayout />
    </>
  );
};

const LegacyOrgRedirect: React.FC<{ section?: 'overview' | 'projects' | 'members' | 'settings' }> = ({ section }) => {
  const { currentOrg } = useAuth();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab') || section;
  if (!currentOrg) return null;
  if (tab === 'team' || tab === 'members') {
    return <Navigate to={`/organization/${currentOrg.id}/members`} replace />;
  }
  if (tab === 'projects') {
    return <Navigate to={`/organization/${currentOrg.id}/projects`} replace />;
  }
  if (tab === 'settings') {
    return <Navigate to={`/organization/${currentOrg.id}/settings`} replace />;
  }
  return <Navigate to={`/organization/${currentOrg.id}`} replace />;
};

const LegacyProjectRedirect: React.FC<{ subpage?: string }> = ({ subpage }) => {
  const { currentOrg, currentProject, projects } = useAuth();
  if (!currentOrg) return null;
  const proj = currentProject || (projects.length > 0 ? projects[0] : null);
  if (!proj) {
    return <Navigate to={`/organization/${currentOrg.id}`} replace />;
  }
  const target = subpage
    ? `/organization/${currentOrg.id}/projects/${proj.id}/${subpage}`
    : `/organization/${currentOrg.id}/projects/${proj.id}`;
  return <Navigate to={target} replace />;
};

const LegacyTableRedirect: React.FC = () => {
  const { currentOrg, currentProject, projects } = useAuth();
  const { tableId, tab } = useParams();
  if (!currentOrg) return null;
  const proj = currentProject || (projects.length > 0 ? projects[0] : null);
  if (!proj) {
    return <Navigate to={`/organization/${currentOrg.id}`} replace />;
  }
  const tabPart = tab ? `/${tab}` : '';
  return <Navigate to={`/organization/${currentOrg.id}/projects/${proj.id}/tables/${tableId || ''}${tabPart}`} replace />;
};

export const App: React.FC = () => {
  return (
    <ThemeProvider>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AuthProvider>
          <ToastProvider>
          <Routes>
            {/* Public Auth & Onboarding Routes */}
            <Route path="/login" element={<LoginView />} />
            <Route path="/register" element={<RegisterView />} />
            <Route path="/forgot-password" element={<ForgotPasswordView />} />
            <Route path="/reset-password" element={<ResetPasswordView />} />
            <Route path="/invite/:token" element={<InviteAcceptView />} />

            {/* Authenticated Application Routes */}
            <Route path="/" element={<ProtectedLayout />}>
              <Route index element={<HomeView />} />

              {/* Dedicated Organization Level Routes */}
              <Route path="organization/:orgId" element={<OrganizationView section="overview" />} />
              <Route path="organization/:orgId/overview" element={<OrganizationView section="overview" />} />
              <Route path="organization/:orgId/projects" element={<OrganizationView section="projects" />} />
              <Route path="organization/:orgId/members" element={<OrganizationView section="members" />} />
              <Route path="organization/:orgId/settings" element={<OrganizationView section="settings" />} />

              {/* Dedicated Project Level Routes */}
              <Route path="organization/:orgId/projects/:projectId" element={<DashboardView />} />
              <Route path="organization/:orgId/projects/:projectId/dashboard" element={<DashboardView />} />
              <Route path="organization/:orgId/projects/:projectId/datastores" element={<ProjectDatastoresView />} />
              <Route path="organization/:orgId/projects/:projectId/tables" element={<ProjectDatastoresView />} />
              <Route path="organization/:orgId/projects/:projectId/tables/:tableId" element={<TableView />} />
              <Route path="organization/:orgId/projects/:projectId/tables/:tableId/:tab" element={<TableView />} />
              <Route path="organization/:orgId/projects/:projectId/analytics" element={<AnalyticsView />} />
              <Route path="organization/:orgId/projects/:projectId/delivery-logs" element={<DeliveryLogsView />} />
              <Route path="organization/:orgId/projects/:projectId/providers" element={<RulesView />} />
              <Route path="organization/:orgId/projects/:projectId/rules" element={<RulesView />} />
              <Route path="organization/:orgId/projects/:projectId/settings" element={<SettingsView />} />

              {/* Global Account & Super Admin */}
              <Route path="account" element={<AccountView section="general" />} />
              <Route path="account/general" element={<AccountView section="general" />} />
              <Route path="account/security" element={<AccountView section="security" />} />
              <Route path="account/billing" element={<AccountView section="billing" />} />
              <Route path="account/subscriptions" element={<AccountView section="billing" />} />
              <Route path="account/organizations" element={<AccountView section="organizations" />} />

              <Route path="admin" element={<AdminView section="overview" />} />
              <Route path="admin/overview" element={<AdminView section="overview" />} />
              <Route path="admin/tenants" element={<AdminView section="tenants" />} />
              <Route path="admin/tenants/:id" element={<TenantDetailView />} />
              <Route path="admin/organizations" element={<AdminView section="organizations" />} />
              <Route path="admin/organizations/:id" element={<AdminOrganizationDetailView />} />
              <Route path="admin/plans" element={<AdminPlansView />} />
              <Route path="admin/queues" element={<AdminView section="queues" />} />

              {/* Legacy Route Fallbacks */}
              <Route path="organization" element={<LegacyOrgRedirect />} />
              <Route path="dashboard" element={<LegacyProjectRedirect />} />
              <Route path="tables" element={<LegacyProjectRedirect subpage="datastores" />} />
              <Route path="datastores" element={<LegacyProjectRedirect subpage="datastores" />} />
              <Route path="tables/:tableId" element={<LegacyTableRedirect />} />
              <Route path="tables/:tableId/:tab" element={<LegacyTableRedirect />} />
              <Route path="analytics" element={<LegacyProjectRedirect subpage="analytics" />} />
              <Route path="delivery-logs" element={<LegacyProjectRedirect subpage="delivery-logs" />} />
              <Route path="rules" element={<LegacyProjectRedirect subpage="rules" />} />
              <Route path="settings" element={<LegacyProjectRedirect subpage="settings" />} />
              <Route path="team" element={<LegacyOrgRedirect section="members" />} />
            </Route>

            {/* Catch-all Wildcard Route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  </ThemeProvider>
  );
};
